import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getUserProfile } from '../api/Users';
import { getAllProfessions, Profession } from '../api/Professions';
import { User, Class } from '../types';
import api from '../utils/api';
import { useAuth } from '../useAuth';
import { ImpersonatedUser } from '../context/AuthContext';
import { getStateByID } from '../constants/statesData';

const stateLabel = (id: number | undefined): string =>
  id != null ? (getStateByID(id)?.abbreviation ?? `State ${id}`) : '—';

const fmtDate = (val: string | undefined) => {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d.getTime())) return val;
  return `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}/${d.getUTCFullYear()}`;
};

const fmtDateTime = (val: string | Date | undefined) => {
  if (!val) return '—';
  const d = new Date(val as string);
  if (isNaN(d.getTime())) return String(val);
  const mm  = String(d.getMonth() + 1).padStart(2, '0');
  const dd  = String(d.getDate()).padStart(2, '0');
  const hh  = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${mm}/${dd}/${d.getFullYear()} ${hh}:${min}`;
};

// Extract creation timestamp from MongoDB ObjectId (covers pre-timestamps records).
// ObjectId first 4 bytes = Unix timestamp in seconds.
const createdAtFromRecord = (record: any): string => {
  if (record?.createdAt) return fmtDateTime(record.createdAt);
  const id = record?._id;
  if (id && typeof id === 'string' && id.length === 24) {
    const ts = parseInt(id.substring(0, 8), 16) * 1000;
    const d  = new Date(ts);
    if (!isNaN(d.getTime())) return fmtDateTime(d);
  }
  return '—';
};

const PLAN_BADGE: Record<string, string> = {
  free: 'bg-secondary',
  pro: 'bg-primary',
  team: 'bg-success',
};

interface Cycle {
  _id: string;
  cycleid: number;
  cyclename?: string;
  cyclestart: string;
  cycleend: string;
  professionID: string;
  stateid: number;
  state?: string;
  isDeleted?: boolean;
  deletedAt?: string;
  createdAt?: string;
}

interface CycleForm {
  cyclename: string;
  professionID: string;
  stateid: string;
  cyclestart: string;
  cycleend: string;
}

const BLANK_CYCLE_FORM: CycleForm = { cyclename: '', professionID: '', stateid: '', cyclestart: '', cycleend: '' };

interface License {
  _id?: string;
  professionID: string;
  stateID: number;
  licenseNumber: string;
  status: string;
  issueDate?: string;
  expiryDate?: string;
  isDeleted?: boolean;
  deletedAt?: string;
  createdAt?: string;
}

interface LicenseForm {
  professionID: string;
  stateID: string;
  licenseNumber: string;
  status: string;
  issueDate: string;
  expiryDate: string;
}

const BLANK_LICENSE_FORM: LicenseForm = {
  professionID: 'ARCH', stateID: '', licenseNumber: '', status: 'active', issueDate: '', expiryDate: '',
};

interface ClassForm {
  classname: string;
  classdate: string;
  classLU: string;
  categories: string;
  took: boolean;
}

const BLANK_CLASS_FORM: ClassForm = { classname: '', classdate: '', classLU: '', categories: '', took: false };

interface PermDeleteTarget {
  entityType: 'Cycle' | 'Class' | 'License';
  entityId: string;
  entityLabel: string;
}

const AdminUserView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  const [impersonating, setImpersonating] = useState(false);

  const handleImpersonate = async () => {
    if (!id) return;
    setImpersonating(true);
    try {
      const adminToken = localStorage.getItem('token')!;
      const res = await api.post(`/api/users/impersonate/${id}`);
      const { token, impersonatedUser } = res.data as { token: string; impersonatedUser: ImpersonatedUser };
      await startImpersonation(token, adminToken, impersonatedUser);
      navigate('/dashboard');
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Failed to start impersonation.');
    } finally {
      setImpersonating(false);
    }
  };

  const [user, setUser] = useState<User | null>(null);
  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'cycles' | 'classes' | 'emails'>('cycles');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Change-plan modal
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planTarget, setPlanTarget] = useState<'free' | 'solo' | 'pro' | 'team'>('free');
  const [planTier, setPlanTier] = useState<'starter' | 'firm' | 'practice'>('starter');
  const [planAdminPw, setPlanAdminPw] = useState('');
  const [planSaving, setPlanSaving] = useState(false);
  const [planError, setPlanError] = useState('');

  // Override modal
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideMaxCycles, setOverrideMaxCycles] = useState('');
  const [overrideMaxStates, setOverrideMaxStates] = useState('');
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [overrideError, setOverrideError] = useState('');

  // Cycle CRUD
  const [cycleModal, setCycleModal] = useState<'closed' | 'create' | 'edit'>('closed');
  const [cycleForm, setCycleForm] = useState<CycleForm>(BLANK_CYCLE_FORM);
  const [editingCycleId, setEditingCycleId] = useState<string | null>(null);
  const [cycleSaving, setCycleSaving] = useState(false);
  const [cycleFormError, setCycleFormError] = useState('');
  const [cycleDeleting, setCycleDeleting] = useState(false);
  const [cycleDeleteConfirming, setCycleDeleteConfirming] = useState(false);
  const [cycleDeleteConfirmText, setCycleDeleteConfirmText] = useState('');

  // Deleted cycles panel
  const [showDeletedCycles, setShowDeletedCycles] = useState(false);
  const [deletedCycles, setDeletedCycles] = useState<Cycle[]>([]);
  const [deletedCyclesLoading, setDeletedCyclesLoading] = useState(false);
  const [cycleRestoring, setCycleRestoring] = useState<string | null>(null);

  // License CRUD
  const [licenseModal, setLicenseModal] = useState<'closed' | 'create' | 'edit'>('closed');
  const [licenseForm, setLicenseForm] = useState<LicenseForm>(BLANK_LICENSE_FORM);
  const [editingLicenseId, setEditingLicenseId] = useState<string | null>(null);
  const [licenseSaving, setLicenseSaving] = useState(false);
  const [licenseFormError, setLicenseFormError] = useState('');
  const [licenseDeleting, setLicenseDeleting] = useState(false);
  const [licenseDeleteConfirming, setLicenseDeleteConfirming] = useState(false);
  const [licenseDeleteConfirmText, setLicenseDeleteConfirmText] = useState('');

  // Deleted licenses panel
  const [showDeletedLicenses, setShowDeletedLicenses] = useState(false);
  const [deletedLicenses, setDeletedLicenses] = useState<License[]>([]);
  const [deletedLicensesLoading, setDeletedLicensesLoading] = useState(false);
  const [licenseRestoring, setLicenseRestoring] = useState<string | null>(null);

  // Class CRUD
  const [classModal, setClassModal] = useState<'closed' | 'create' | 'edit'>('closed');
  const [classForm, setClassForm] = useState<ClassForm>(BLANK_CLASS_FORM);
  const [editingClassId, setEditingClassId] = useState<string | null>(null);
  const [classSaving, setClassSaving] = useState(false);
  const [classFormError, setClassFormError] = useState('');
  const [classDeleting, setClassDeleting] = useState(false);
  const [classDeleteConfirming, setClassDeleteConfirming] = useState(false);
  const [classDeleteConfirmText, setClassDeleteConfirmText] = useState('');

  // Deleted classes panel
  const [showDeletedClasses, setShowDeletedClasses] = useState(false);
  const [deletedClasses, setDeletedClasses] = useState<any[]>([]);
  const [deletedClassesLoading, setDeletedClassesLoading] = useState(false);
  const [classRestoring, setClassRestoring] = useState<string | null>(null);

  // Permanent delete modal
  const [permDeleteTarget, setPermDeleteTarget] = useState<PermDeleteTarget | null>(null);
  const [permDeletePhrase, setPermDeletePhrase] = useState('');
  const [permDeletePw, setPermDeletePw] = useState('');
  const [permDeleting, setPermDeleting] = useState(false);
  const [permDeleteError, setPermDeleteError] = useState('');

  // Email history
  const [emailLogs, setEmailLogs] = useState<any[]>([]);
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);
  const [emailLogsLoaded, setEmailLogsLoaded] = useState(false);

  // Onboarding email modal
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [onboardingPreviewHtml, setOnboardingPreviewHtml] = useState('');
  const [onboardingCandidate, setOnboardingCandidate] = useState<any>(null);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [onboardingConfirmed, setOnboardingConfirmed] = useState(false);
  const [onboardingSending, setOnboardingSending] = useState(false);
  const [onboardingSentMsg, setOnboardingSentMsg] = useState('');
  const [onboardingError, setOnboardingError] = useState('');

  // Profession-activated notify modal
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifyProfessionID, setNotifyProfessionID] = useState('LCSW');
  const [notifyProfessionName, setNotifyProfessionName] = useState('Licensed Clinical Social Worker');
  const [notifyStatesInput, setNotifyStatesInput] = useState('');
  const [notifyPreviewHtml, setNotifyPreviewHtml] = useState('');
  const [notifyPreviewLoading, setNotifyPreviewLoading] = useState(false);
  const [notifyConfirmed, setNotifyConfirmed] = useState(false);
  const [notifySending, setNotifySending] = useState(false);
  const [notifySentMsg, setNotifySentMsg] = useState('');
  const [notifyError, setNotifyError] = useState('');
  const [notifyTestMode, setNotifyTestMode] = useState<{ active: boolean; allowedEmails: string[] } | null>(null);

  const [professions, setProfessions] = useState<Profession[]>([]);
  const professionLabel = (pid: string | undefined): string => {
    if (!pid) return '—';
    const match = professions.find(p => p.professionID === pid.toUpperCase());
    return match ? `${match.name} (${pid.toUpperCase()})` : pid.toUpperCase();
  };

  // ── Data loading ────────────────────────────────────────────────────────────
  useEffect(() => {
    getAllProfessions().then(setProfessions).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      getUserProfile(id),
      api.get(`/api/cycles/user/${id}`),
      api.get(`/api/classes/user/${id}`),
    ])
      .then(([userData, cyclesRes, classesRes]) => {
        setUser(userData);
        setCycles(cyclesRes.data || []);
        setClasses(classesRes.data || []);
      })
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load user data.'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const payload = JSON.parse(atob(token.split('.')[1]));
      const adminId = payload?.user?.id;
      if (adminId) {
        api.get(`/api/users/${adminId}`)
          .then(res => setIsSuperAdmin(res.data?.isSuperAdmin === true))
          .catch(() => {});
      }
    } catch {}
  }, []);

  // ── Cycle handlers ──────────────────────────────────────────────────────────
  const closeCycleModal = () => {
    setCycleModal('closed');
    setCycleDeleteConfirming(false);
    setCycleDeleteConfirmText('');
  };

  const openCreateCycle = () => {
    setCycleForm(BLANK_CYCLE_FORM);
    setCycleFormError('');
    setEditingCycleId(null);
    setCycleDeleteConfirming(false);
    setCycleDeleteConfirmText('');
    setCycleModal('create');
  };

  const openEditCycle = (cycle: Cycle) => {
    setCycleForm({
      cyclename:    cycle.cyclename ?? '',
      professionID: cycle.professionID ?? '',
      stateid:      String(cycle.stateid ?? ''),
      cyclestart:   cycle.cyclestart ? cycle.cyclestart.slice(0, 10) : '',
      cycleend:     cycle.cycleend   ? cycle.cycleend.slice(0, 10)   : '',
    });
    setCycleFormError('');
    setEditingCycleId(cycle._id);
    setCycleDeleteConfirming(false);
    setCycleDeleteConfirmText('');
    setCycleModal('edit');
  };

  const handleSaveCycle = async () => {
    if (!cycleForm.cyclename.trim()) { setCycleFormError('Cycle name is required.'); return; }
    if (!cycleForm.stateid)          { setCycleFormError('State is required.'); return; }
    setCycleSaving(true);
    setCycleFormError('');
    try {
      const payload = {
        cyclename:    cycleForm.cyclename.trim(),
        professionID: cycleForm.professionID.trim().toUpperCase(),
        stateid:      Number(cycleForm.stateid),
        cyclestart:   cycleForm.cyclestart || undefined,
        cycleend:     cycleForm.cycleend   || undefined,
      };
      if (cycleModal === 'create') {
        const res = await api.post(`/api/cycles/admin/for-user/${id}`, payload);
        setCycles(prev => [...prev, res.data]);
      } else if (editingCycleId) {
        const res = await api.put(`/api/cycles/${editingCycleId}`, payload);
        setCycles(prev => prev.map(c => c._id === editingCycleId ? { ...c, ...res.data } : c));
      }
      closeCycleModal();
    } catch (err: any) {
      setCycleFormError(err?.response?.data?.error || 'Save failed.');
    } finally {
      setCycleSaving(false);
    }
  };

  const handleDeleteCycle = async () => {
    if (!editingCycleId) return;
    setCycleDeleting(true);
    try {
      await api.delete(`/api/cycles/${editingCycleId}`);
      setCycles(prev => prev.filter(c => c._id !== editingCycleId));
      closeCycleModal();
    } catch (err: any) {
      setCycleFormError(err?.response?.data?.error || 'Delete failed.');
    } finally {
      setCycleDeleting(false);
    }
  };

  const loadDeletedCycles = async () => {
    setDeletedCyclesLoading(true);
    try {
      const res = await api.get(`/api/cycles/user/${id}/deleted`);
      setDeletedCycles(res.data || []);
    } catch {}
    finally { setDeletedCyclesLoading(false); }
  };

  const handleToggleDeletedCycles = () => {
    if (!showDeletedCycles) loadDeletedCycles();
    setShowDeletedCycles(v => !v);
  };

  const handleRestoreCycle = async (cycleId: string) => {
    setCycleRestoring(cycleId);
    try {
      await api.put(`/api/cycles/${cycleId}/restore`);
      const restored = deletedCycles.find(c => c._id === cycleId);
      if (restored) {
        setDeletedCycles(prev => prev.filter(c => c._id !== cycleId));
        setCycles(prev => [...prev, { ...restored, isDeleted: false }]);
      }
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Restore failed.');
    } finally {
      setCycleRestoring(null);
    }
  };

  // ── License handlers ────────────────────────────────────────────────────────
  const closeLicenseModal = () => {
    setLicenseModal('closed');
    setLicenseDeleteConfirming(false);
    setLicenseDeleteConfirmText('');
    setLicenseFormError('');
  };

  const openCreateLicense = () => {
    setLicenseForm(BLANK_LICENSE_FORM);
    setLicenseFormError('');
    setEditingLicenseId(null);
    setLicenseDeleteConfirming(false);
    setLicenseDeleteConfirmText('');
    setLicenseModal('create');
  };

  const openEditLicense = (lic: License) => {
    setLicenseForm({
      professionID: lic.professionID ?? 'ARCH',
      stateID:      String(lic.stateID ?? ''),
      licenseNumber: lic.licenseNumber ?? '',
      status:       lic.status ?? 'active',
      issueDate:    lic.issueDate ? lic.issueDate.slice(0, 10) : '',
      expiryDate:   lic.expiryDate ? lic.expiryDate.slice(0, 10) : '',
    });
    setLicenseFormError('');
    setEditingLicenseId(lic._id ?? null);
    setLicenseDeleteConfirming(false);
    setLicenseDeleteConfirmText('');
    setLicenseModal('edit');
  };

  const handleSaveLicense = async () => {
    if (!licenseForm.licenseNumber.trim()) { setLicenseFormError('License number is required.'); return; }
    if (!licenseForm.stateID)              { setLicenseFormError('State ID is required.'); return; }
    setLicenseSaving(true);
    setLicenseFormError('');
    try {
      const payload = {
        professionID: licenseForm.professionID.trim().toUpperCase(),
        stateID:      Number(licenseForm.stateID),
        licenseNumber: licenseForm.licenseNumber.trim(),
        status:       licenseForm.status,
        issueDate:    licenseForm.issueDate || undefined,
        expiryDate:   licenseForm.expiryDate || undefined,
      };
      if (licenseModal === 'create') {
        const res = await api.post(`/api/users/${id}/licenses`, payload);
        setUser(u => u ? { ...u, licenses: [...(u.licenses || []), res.data] } : u);
      } else if (editingLicenseId) {
        const res = await api.put(`/api/users/${id}/licenses/${editingLicenseId}`, payload);
        setUser(u => u ? {
          ...u,
          licenses: (u.licenses || []).map((l: any) => l._id === editingLicenseId ? res.data : l),
        } : u);
      }
      closeLicenseModal();
    } catch (err: any) {
      setLicenseFormError(err?.response?.data?.error || 'Save failed.');
    } finally {
      setLicenseSaving(false);
    }
  };

  const handleDeleteLicense = async () => {
    if (!editingLicenseId) return;
    setLicenseDeleting(true);
    try {
      await api.delete(`/api/users/${id}/licenses/${editingLicenseId}`);
      setUser(u => u ? {
        ...u,
        licenses: (u.licenses || []).filter((l: any) => l._id !== editingLicenseId),
      } : u);
      closeLicenseModal();
    } catch (err: any) {
      setLicenseFormError(err?.response?.data?.error || 'Delete failed.');
    } finally {
      setLicenseDeleting(false);
    }
  };

  const loadDeletedLicenses = async () => {
    setDeletedLicensesLoading(true);
    try {
      const res = await api.get(`/api/users/${id}/licenses/deleted`);
      setDeletedLicenses(res.data || []);
    } catch {}
    finally { setDeletedLicensesLoading(false); }
  };

  const handleToggleDeletedLicenses = () => {
    if (!showDeletedLicenses) loadDeletedLicenses();
    setShowDeletedLicenses(v => !v);
  };

  const handleRestoreLicense = async (licenseId: string) => {
    setLicenseRestoring(licenseId);
    try {
      await api.put(`/api/users/${id}/licenses/${licenseId}/restore`);
      const restored = deletedLicenses.find(l => l._id === licenseId);
      if (restored) {
        setDeletedLicenses(prev => prev.filter(l => l._id !== licenseId));
        setUser(u => u
          ? ({ ...u, licenses: [...(u.licenses || []), { ...restored, isDeleted: false }] } as unknown as User)
          : u
        );
      }
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Restore failed.');
    } finally {
      setLicenseRestoring(null);
    }
  };

  // ── Class handlers ──────────────────────────────────────────────────────────
  const closeClassModal = () => {
    setClassModal('closed');
    setClassDeleteConfirming(false);
    setClassDeleteConfirmText('');
    setClassFormError('');
  };

  const openCreateClass = () => {
    setClassForm(BLANK_CLASS_FORM);
    setClassFormError('');
    setEditingClassId(null);
    setClassDeleteConfirming(false);
    setClassDeleteConfirmText('');
    setClassModal('create');
  };

  const openEditClass = (cls: any) => {
    setClassForm({
      classname: cls.classname ?? '',
      classdate: cls.classdate ? cls.classdate.slice(0, 10) : '',
      classLU:   String(cls.classLU ?? ''),
      categories: (cls.categories || []).join(', '),
      took:      cls.took ?? false,
    });
    setClassFormError('');
    setEditingClassId(cls._id ?? null);
    setClassDeleteConfirming(false);
    setClassDeleteConfirmText('');
    setClassModal('edit');
  };

  const handleSaveClass = async () => {
    if (!classForm.classname.trim()) { setClassFormError('Course name is required.'); return; }
    if (!classForm.classdate)        { setClassFormError('Date is required.'); return; }
    setClassSaving(true);
    setClassFormError('');
    try {
      const payload = {
        classname:  classForm.classname.trim(),
        classdate:  classForm.classdate,
        classLU:    classForm.classLU ? Number(classForm.classLU) : 0,
        categories: classForm.categories.split(',').map(s => s.trim()).filter(Boolean),
        took:       classForm.took,
      };
      if (classModal === 'create') {
        const res = await api.post(`/api/classes/admin/for-user/${id}`, payload);
        setClasses(prev => [...prev, res.data]);
      } else if (editingClassId) {
        const res = await api.put(`/api/classes/${editingClassId}`, payload);
        setClasses(prev => prev.map(c => (c as any)._id === editingClassId ? { ...c, ...res.data } : c));
      }
      closeClassModal();
    } catch (err: any) {
      setClassFormError(err?.response?.data?.error || 'Save failed.');
    } finally {
      setClassSaving(false);
    }
  };

  const handleDeleteClass = async () => {
    if (!editingClassId) return;
    setClassDeleting(true);
    try {
      await api.delete(`/api/classes/${editingClassId}`);
      setClasses(prev => prev.filter(c => (c as any)._id !== editingClassId));
      closeClassModal();
    } catch (err: any) {
      setClassFormError(err?.response?.data?.error || 'Delete failed.');
    } finally {
      setClassDeleting(false);
    }
  };

  const loadDeletedClasses = async () => {
    setDeletedClassesLoading(true);
    try {
      const res = await api.get(`/api/classes/admin/deleted/user/${id}`);
      setDeletedClasses(res.data || []);
    } catch {}
    finally { setDeletedClassesLoading(false); }
  };

  const handleToggleDeletedClasses = () => {
    if (!showDeletedClasses) loadDeletedClasses();
    setShowDeletedClasses(v => !v);
  };

  const handleRestoreClass = async (classId: string) => {
    setClassRestoring(classId);
    try {
      await api.put(`/api/classes/admin/${classId}/restore`);
      const restored = deletedClasses.find(c => c._id === classId);
      if (restored) {
        setDeletedClasses(prev => prev.filter(c => c._id !== classId));
        setClasses(prev => [...prev, { ...restored, isDeleted: false }]);
      }
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Restore failed.');
    } finally {
      setClassRestoring(null);
    }
  };

  // ── Permanent delete ────────────────────────────────────────────────────────
  const openPermDelete = (target: PermDeleteTarget) => {
    setPermDeleteTarget(target);
    setPermDeletePhrase('');
    setPermDeletePw('');
    setPermDeleteError('');
  };

  const closePermDelete = () => {
    setPermDeleteTarget(null);
    setPermDeletePhrase('');
    setPermDeletePw('');
    setPermDeleteError('');
  };

  const handlePermDelete = async () => {
    if (!permDeleteTarget) return;
    setPermDeleting(true);
    setPermDeleteError('');
    try {
      const body = { confirmPhrase: permDeletePhrase, adminPassword: permDeletePw };
      const { entityType, entityId } = permDeleteTarget;

      if (entityType === 'Cycle') {
        await api.delete(`/api/cycles/permanent/${entityId}`, { data: body });
        setDeletedCycles(prev => prev.filter(c => c._id !== entityId));
      } else if (entityType === 'Class') {
        await api.delete(`/api/classes/admin/permanent/${entityId}`, { data: body });
        setDeletedClasses(prev => prev.filter(c => c._id !== entityId));
      } else if (entityType === 'License') {
        await api.delete(`/api/users/permanent/${id}/licenses/${entityId}`, { data: body });
        setDeletedLicenses(prev => prev.filter(l => l._id !== entityId));
      }
      closePermDelete();
    } catch (err: any) {
      setPermDeleteError(err?.response?.data?.error || 'Permanent delete failed.');
    } finally {
      setPermDeleting(false);
    }
  };

  // ── Plan / override handlers ────────────────────────────────────────────────
  const openPlanModal = () => {
    if (!user) return;
    setPlanTarget((user.subscription?.plan ?? 'free') as 'free' | 'solo' | 'pro' | 'team');
    setPlanTier(((user.subscription as any)?.tier ?? 'starter') as 'starter' | 'firm' | 'practice');
    setPlanAdminPw('');
    setPlanError('');
    setShowPlanModal(true);
  };

  const handleSavePlan = async () => {
    if (!planAdminPw.trim()) { setPlanError('Enter your admin password.'); return; }
    setPlanSaving(true);
    setPlanError('');
    try {
      const body: Record<string, any> = { plan: planTarget, adminPassword: planAdminPw };
      if (planTarget === 'team') body.tier = planTier;
      const res = await api.put(`/api/users/${id}/plan`, body);
      setUser(u => u ? { ...u, subscription: res.data.subscription } : u);
      setShowPlanModal(false);
    } catch (err: any) {
      const code = err?.response?.data?.code;
      setPlanError(code === 'WRONG_PASSWORD' ? 'Incorrect password.' : err?.response?.data?.error || 'Failed to update plan.');
    } finally {
      setPlanSaving(false);
    }
  };

  const openOverrideModal = () => {
    if (!user) return;
    setOverrideMaxCycles(user.planOverrides?.maxCycles != null ? String(user.planOverrides.maxCycles) : '');
    setOverrideMaxStates(user.planOverrides?.maxStates != null ? String(user.planOverrides.maxStates) : '');
    setOverrideError('');
    setShowOverrideModal(true);
  };

  const handleSaveOverrides = async () => {
    setOverrideSaving(true);
    setOverrideError('');
    try {
      const body = {
        maxCycles: overrideMaxCycles.trim() !== '' ? parseInt(overrideMaxCycles, 10) : null,
        maxStates: overrideMaxStates.trim() !== '' ? parseInt(overrideMaxStates, 10) : null,
      };
      const res = await api.put(`/api/users/${id}/overrides`, body);
      setUser(u => u ? { ...u, planOverrides: res.data.planOverrides } : u);
      setShowOverrideModal(false);
    } catch (err: any) {
      setOverrideError(err?.response?.data?.error || 'Failed to update overrides.');
    } finally {
      setOverrideSaving(false);
    }
  };

  // ── Onboarding email handlers ───────────────────────────────────────────────
  const handleOpenOnboardingPreview = async () => {
    if (!id) return;
    setOnboardingLoading(true);
    setOnboardingError('');
    setOnboardingSentMsg('');
    setOnboardingConfirmed(false);
    setShowOnboardingModal(true);
    try {
      const res = await api.get(`/api/onboarding/cycle-setup/preview-email/${id}`);
      setOnboardingPreviewHtml(res.data.html);
      setOnboardingCandidate(res.data.candidate);
    } catch (err: any) {
      setOnboardingError(err?.response?.data?.error || 'Failed to load email preview.');
    } finally {
      setOnboardingLoading(false);
    }
  };

  const handleSendOnboardingEmail = async () => {
    if (!id || !onboardingConfirmed) return;
    setOnboardingSending(true);
    setOnboardingError('');
    try {
      const res = await api.post(`/api/onboarding/cycle-setup/send/${id}`);
      setOnboardingSentMsg(`Email sent to ${res.data.email}`);
      setOnboardingConfirmed(false);
    } catch (err: any) {
      setOnboardingError(err?.response?.data?.error || 'Failed to send email.');
    } finally {
      setOnboardingSending(false);
    }
  };

  // ── Profession-activated notify handlers ──────────────────────────────────
  const handleOpenNotifyModal = async () => {
    setNotifyPreviewHtml('');
    setNotifyConfirmed(false);
    setNotifySentMsg('');
    setNotifyError('');
    setNotifyStatesInput('');
    setNotifyTestMode(null);
    setShowNotifyModal(true);
    try {
      const res = await api.get('/api/system-config/email-mode');
      setNotifyTestMode({
        active: res.data.emailTestMode === true,
        allowedEmails: res.data.testEmails || [],
      });
    } catch {
      // non-fatal — just won't show the banner
    }
  };

  const handleNotifyPreview = async () => {
    if (!id || !notifyProfessionID.trim() || !notifyProfessionName.trim() || !notifyStatesInput.trim()) return;
    setNotifyPreviewLoading(true);
    setNotifyError('');
    setNotifyPreviewHtml('');
    try {
      const states = notifyStatesInput.split(',').map(s => s.trim()).filter(Boolean).join(',');
      const res = await api.get(`/api/onboarding/profession-activated/preview/${id}`, {
        params: { professionID: notifyProfessionID.trim(), professionName: notifyProfessionName.trim(), states },
      });
      setNotifyPreviewHtml(res.data.html);
    } catch (err: any) {
      setNotifyError(err?.response?.data?.error || 'Failed to load preview.');
    } finally {
      setNotifyPreviewLoading(false);
    }
  };

  const handleSendNotifyEmail = async () => {
    if (!id || !notifyConfirmed) return;
    setNotifySending(true);
    setNotifyError('');
    try {
      const states = notifyStatesInput.split(',').map(s => s.trim()).filter(Boolean);
      const res = await api.post(`/api/onboarding/profession-activated/send/${id}`, {
        professionID: notifyProfessionID.trim(),
        professionName: notifyProfessionName.trim(),
        states,
      });
      const sentTo = res.data.redirectedTo
        ? `Sent to ${res.data.redirectedTo} (test redirect — original recipient: ${res.data.email})`
        : `Email sent to ${res.data.email}`;
      setNotifySentMsg(sentTo);
      setNotifyConfirmed(false);
    } catch (err: any) {
      setNotifyError(err?.response?.data?.error || 'Failed to send email.');
    } finally {
      setNotifySending(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await api.get(`/api/cycles/user/${id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${user?.firstName}_${user?.lastName}_compliance.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Failed to download PDF.');
    }
  };

  // ── Early returns ───────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="container mt-4 text-center">
        <div className="spinner-border" role="status" />
        <p className="mt-3">Loading user data…</p>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <h4>Error</h4>
          <p>{error || 'User not found'}</p>
          <button className="btn btn-primary" onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    );
  }

  const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
  const completedClasses = classes.filter((c: Class) => c.took);
  const totalCredits = Math.round(completedClasses.reduce((sum, c) => sum + (c.classLU || 0), 0) * 100) / 100;
  const currentPlan = user.subscription?.plan ?? 'free';
  const isFree = currentPlan === 'free';
  const isTrialing = user.subscription?.status === 'trialing';
  const activeLicenses = (user.licenses || []).filter((l: any) => !l.isDeleted);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="container mt-4 pb-5">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-2">
        <div>
          <button className="btn btn-sm btn-outline-secondary mb-2" onClick={() => navigate(-1)}>← Back</button>
          <h2 className="mb-0">{fullName}</h2>
          <p className="text-muted mb-0">{user.email}</p>
          {isSuperAdmin && (
            <span className="badge bg-danger mt-1" style={{ fontSize: '0.7rem' }}>SUPER ADMIN MODE</span>
          )}
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <button
            className="btn btn-sm"
            style={{ background: '#92400e', color: '#fef3c7', border: 'none' }}
            onClick={handleImpersonate}
            disabled={impersonating}
          >
            {impersonating ? 'Starting…' : '👁 View as User'}
          </button>
          <Link to={`/admin/users/${id}`} className="btn btn-outline-primary btn-sm">✏️ Edit Profile</Link>
          <Link to={`/admin/compliance-report/${id}`} className="btn btn-outline-info btn-sm">📄 Compliance Report</Link>
          {cycles.length > 0 && (
            <button className="btn btn-outline-success btn-sm" onClick={handleDownloadPDF}>⬇ PDF</button>
          )}
          {(user.licenses?.length ?? 0) > 0 && cycles.length === 0 && (
            <button className="btn btn-warning btn-sm" onClick={handleOpenOnboardingPreview}>✉️ Send Onboarding Email</button>
          )}
        </div>
      </div>

      {/* Profile summary stats */}
      <div className="row g-3 mb-4">
        {[
          { value: cycles.length,           label: 'Cycles',        colorClass: 'cec-card-navy'  },
          { value: classes.length,          label: 'Total Classes', colorClass: 'cec-card-cyan'  },
          { value: completedClasses.length, label: 'Completed',     colorClass: 'cec-card-green' },
          { value: totalCredits.toFixed(1), label: 'Credits Earned',colorClass: 'cec-card-amber' },
        ].map(({ value, label, colorClass }) => (
          <div className="col-md-3 col-6" key={label}>
            <div className={`card cec-stat-card ${colorClass}`}>
              <div className="card-body p-3">
                <div className="cec-stat-label">{label}</div>
                <div className="cec-stat-value">{value}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Profile Card */}
      <div className="card mb-4">
        <div className="card-header cec-section-header"><span className="cec-section-title">Profile</span></div>
        <div className="card-body">
          <div className="row g-2 mb-3">
            <div className="col-md-3"><small className="text-muted d-block">Profession</small>{professionLabel(user.professionID)}</div>
            <div className="col-md-3"><small className="text-muted d-block">Phone</small>{user.phone || '—'}</div>
            <div className="col-md-3"><small className="text-muted d-block">Company</small>{user.company || '—'}</div>
            <div className="col-md-3">
              <small className="text-muted d-block">Status</small>
              <span className={`badge ${user.suspended ? 'bg-secondary' : 'bg-success'}`}>
                {user.suspended ? 'Suspended' : 'Active'}
              </span>
            </div>
            <div className="col-md-3"><small className="text-muted d-block">Account Created</small><span className="text-muted" style={{ fontSize: '0.85rem' }}>{fmtDateTime((user as any).createdAt)}</span></div>
            <div className="col-md-3"><small className="text-muted d-block">Last Updated</small><span className="text-muted" style={{ fontSize: '0.85rem' }}>{fmtDateTime((user as any).updatedAt)}</span></div>
          </div>

          {/* Licenses table */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-muted fw-semibold">Licenses ({activeLicenses.length})</small>
            <button className="btn btn-sm btn-outline-primary" onClick={openCreateLicense}>+ Add License</button>
          </div>
          {activeLicenses.length === 0 ? (
            <p className="text-muted small mb-2">No licenses on file.</p>
          ) : (
            <div className="table-responsive mb-2">
              <table className="table cec-table table-sm table-hover mb-0" style={{ fontSize: '0.82rem' }}>
                <thead>
                  <tr>
                    {['Profession','State','License #','Status','Issue','Expiry','Added',''].map(h => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {activeLicenses.map((lic: any, i: number) => (
                    <tr key={lic._id ?? i}>
                      <td><span className="badge bg-info">{lic.professionID}</span></td>
                      <td>{stateLabel(lic.stateID)}</td>
                      <td>{lic.licenseNumber}</td>
                      <td>
                        <span className={`badge ${lic.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                          {lic.status}
                        </span>
                      </td>
                      <td>{fmtDate(lic.issueDate)}</td>
                      <td>{fmtDate(lic.expiryDate)}</td>
                      <td className="text-muted" style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{createdAtFromRecord(lic)}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-secondary py-0 px-1"
                          onClick={() => openEditLicense(lic)}
                          title="Edit license"
                        >
                          ✏
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Deleted licenses toggle */}
          <button
            className="btn btn-link btn-sm text-muted p-0"
            onClick={handleToggleDeletedLicenses}
          >
            {showDeletedLicenses ? '▲ Hide deleted licenses' : '▼ Show deleted licenses'}
          </button>
          {showDeletedLicenses && (
            <div className="mt-2">
              {deletedLicensesLoading ? (
                <div className="spinner-border spinner-border-sm" />
              ) : deletedLicenses.length === 0 ? (
                <p className="text-muted small mb-0">No deleted licenses.</p>
              ) : (
                <table className="table table-sm mb-0" style={{ fontSize: '0.8rem' }}>
                  <thead>
                    <tr>
                      <th>Profession</th><th>State</th><th>License #</th>
                      <th>Deleted</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deletedLicenses.map((lic: License, i: number) => (
                      <tr key={lic._id ?? i} className="table-danger">
                        <td>{lic.professionID}</td>
                        <td>{stateLabel(lic.stateID)}</td>
                        <td>{lic.licenseNumber}</td>
                        <td>{fmtDate(lic.deletedAt)}</td>
                        <td className="d-flex gap-1">
                          <button
                            className="btn btn-sm btn-outline-success py-0 px-1"
                            disabled={licenseRestoring === lic._id}
                            onClick={() => handleRestoreLicense(lic._id!)}
                          >
                            {licenseRestoring === lic._id ? '…' : 'Restore'}
                          </button>
                          {isSuperAdmin && (
                            <button
                              className="btn btn-sm btn-danger py-0 px-1"
                              onClick={() => openPermDelete({
                                entityType: 'License',
                                entityId: lic._id!,
                                entityLabel: `${lic.professionID} #${lic.licenseNumber}`,
                              })}
                            >
                              Perm. Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Subscription Card */}
      <div className="card mb-4">
        <div className="card-header cec-section-header">
          <span className="cec-section-title">Subscription</span>
          <button className="btn btn-sm btn-outline-warning" onClick={openPlanModal}>Change Plan</button>
        </div>
        <div className="card-body">
          <div className="row g-2 align-items-start">
            <div className="col-md-3">
              <small className="text-muted d-block">Plan</small>
              <span className={`badge text-uppercase ${PLAN_BADGE[currentPlan] ?? 'bg-secondary'}`}>{currentPlan}</span>
              {user.subscription?.tier && (
                <span className="badge bg-light text-dark border ms-1">{user.subscription.tier}</span>
              )}
              {isTrialing && (
                <span className="badge ms-1" style={{ background: '#FEF3C7', color: '#B45309', border: '1px solid #F59E0B' }}>Trial — not paying</span>
              )}
            </div>
            <div className="col-md-3">
              <small className="text-muted d-block">Status</small>
              {isTrialing
                ? <span className="badge" style={{ background: '#FEF3C7', color: '#B45309', border: '1px solid #F59E0B' }}>trialing</span>
                : (user.subscription?.status ?? 'active')}
            </div>
            <div className="col-md-3"><small className="text-muted d-block">Billing</small>{isTrialing ? 'No card on file' : (user.subscription?.billingInterval ?? '—')}</div>
            <div className="col-md-3">
              <small className="text-muted d-block">{isTrialing ? 'Trial Ends' : 'Period End'}</small>
              {isTrialing
                ? ((user.subscription as any)?.trialEndsAt ? fmtDate((user.subscription as any).trialEndsAt) : '—')
                : (user.subscription?.currentPeriodEnd ? fmtDate(user.subscription.currentPeriodEnd) : '—')}
            </div>
            {isFree && (
              <div className="col-12 mt-3 pt-2 border-top">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <small className="text-muted fw-semibold">Free-Plan Overrides</small>
                  <button className="btn btn-sm btn-outline-secondary" onClick={openOverrideModal}>Edit Overrides</button>
                </div>
                <div className="d-flex gap-4">
                  <div>
                    <small className="text-muted d-block">Max Cycles</small>
                    <span className={user.planOverrides?.maxCycles != null ? 'text-warning fw-semibold' : 'text-muted'}>
                      {user.planOverrides?.maxCycles != null ? user.planOverrides.maxCycles : '1 (default)'}
                    </span>
                  </div>
                  <div>
                    <small className="text-muted d-block">Max States</small>
                    <span className={user.planOverrides?.maxStates != null ? 'text-warning fw-semibold' : 'text-muted'}>
                      {user.planOverrides?.maxStates != null ? user.planOverrides.maxStates : 'no limit set'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'cycles' ? 'active' : ''}`}
            onClick={() => setActiveTab('cycles')}
          >
            Cycles ({cycles.length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'classes' ? 'active' : ''}`}
            onClick={() => setActiveTab('classes')}
          >
            Classes ({classes.length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'emails' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('emails');
              if (!emailLogsLoaded && !emailLogsLoading) {
                setEmailLogsLoading(true);
                api.get(`/api/users/${id}/email-log`)
                  .then(res => { setEmailLogs(res.data || []); setEmailLogsLoaded(true); })
                  .catch(() => {})
                  .finally(() => setEmailLogsLoading(false));
              }
            }}
          >
            Email History {emailLogsLoaded && `(${emailLogs.length})`}
          </button>
        </li>
      </ul>

      {/* ── Cycles Tab ── */}
      {activeTab === 'cycles' && (
        <div className="card">
          <div className="card-header cec-section-header">
            <span className="cec-section-title">Cycles ({cycles.length})</span>
            <div className="d-flex gap-2">
              {user.email === 'philandujarmsw@gmail.com' && (
                <button className="btn btn-sm btn-outline-warning" onClick={handleOpenNotifyModal} title="Notify user that official requirements are now available">
                  📣 Notify: Official Requirements
                </button>
              )}
              <button className="btn btn-sm btn-outline-primary" onClick={openCreateCycle}>+ Add Cycle</button>
            </div>
          </div>
          <div className="card-body p-0">
            {cycles.length === 0 ? (
              <p className="text-muted p-3 mb-0">No cycles found.</p>
            ) : (
              <div className="table-responsive">
                <table className="table cec-table table-hover table-sm mb-0">
                  <thead>
                    <tr>
                      {['Cycle Name','Profession','State','Start','End','Created','Report'].map(h => (
                        <th key={h}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cycles.map((cycle, _ci) => (
                      <tr key={cycle._id}>
                        <td style={{ cursor: 'pointer' }} onClick={() => openEditCycle(cycle)}>
                          <span className="text-primary">{cycle.cyclename || `Cycle ${cycle.cycleid}`}</span>
                        </td>
                        <td><span className="badge bg-info">{cycle.professionID}</span></td>
                        <td>{cycle.state || stateLabel(cycle.stateid)}</td>
                        <td>{fmtDate(cycle.cyclestart)}</td>
                        <td>{fmtDate(cycle.cycleend)}</td>
                        <td className="text-muted" style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{createdAtFromRecord(cycle)}</td>
                        <td>
                          <Link to={`/admin/compliance-report/${id}`} className="btn btn-sm btn-outline-info">📄</Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Deleted cycles panel */}
            <div className="px-3 pb-2 pt-1 border-top">
              <button className="btn btn-link btn-sm text-muted p-0" onClick={handleToggleDeletedCycles}>
                {showDeletedCycles ? '▲ Hide deleted cycles' : '▼ Show deleted cycles'}
              </button>
              {showDeletedCycles && (
                <div className="mt-2">
                  {deletedCyclesLoading ? (
                    <div className="spinner-border spinner-border-sm" />
                  ) : deletedCycles.length === 0 ? (
                    <p className="text-muted small mb-0">No deleted cycles.</p>
                  ) : (
                    <table className="table table-sm mb-0" style={{ fontSize: '0.8rem' }}>
                      <thead>
                        <tr><th>Name</th><th>Profession</th><th>State</th><th>Deleted</th><th>Actions</th></tr>
                      </thead>
                      <tbody>
                        {deletedCycles.map((cycle) => (
                          <tr key={cycle._id} className="table-danger">
                            <td>{cycle.cyclename || `Cycle ${cycle.cycleid}`}</td>
                            <td>{cycle.professionID}</td>
                            <td>{stateLabel(cycle.stateid)}</td>
                            <td>{fmtDate(cycle.deletedAt)}</td>
                            <td className="d-flex gap-1">
                              <button
                                className="btn btn-sm btn-outline-success py-0 px-1"
                                disabled={cycleRestoring === cycle._id}
                                onClick={() => handleRestoreCycle(cycle._id)}
                              >
                                {cycleRestoring === cycle._id ? '…' : 'Restore'}
                              </button>
                              {isSuperAdmin && (
                                <button
                                  className="btn btn-sm btn-danger py-0 px-1"
                                  onClick={() => openPermDelete({
                                    entityType: 'Cycle',
                                    entityId: cycle._id,
                                    entityLabel: cycle.cyclename || `Cycle ${cycle.cycleid}`,
                                  })}
                                >
                                  Perm. Remove
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Classes Tab ── */}
      {activeTab === 'classes' && (
        <div className="card">
          <div className="card-header cec-section-header">
            <span className="cec-section-title">Classes ({classes.length})</span>
            <button className="btn btn-sm btn-outline-primary" onClick={openCreateClass}>+ Add Class</button>
          </div>
          <div className="card-body p-0">
            {classes.length === 0 ? (
              <p className="text-muted p-3 mb-0">No classes found.</p>
            ) : (
              <div className="table-responsive">
                <table className="table cec-table table-hover table-sm mb-0">
                  <thead>
                    <tr>
                      {['Course Title','Date','Credits','Categories','Status','Created'].map(h => (
                        <th key={h}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {classes
                      .slice()
                      .sort((a, b) => new Date(b.classdate).getTime() - new Date(a.classdate).getTime())
                      .map((cls, _cli) => (
                        <tr key={(cls as any)._id}>
                          <td style={{ cursor: 'pointer' }} onClick={() => openEditClass(cls)}>
                            <span className="text-primary">{cls.classname}</span>
                            {cls.isArchived && <small className="text-muted ms-1">(archived)</small>}
                          </td>
                          <td>{fmtDate(cls.classdate)}</td>
                          <td>{cls.classLU}</td>
                          <td>
                            {cls.categories && cls.categories.length > 0
                              ? cls.categories.map((cat, i) => (
                                  <span key={i} className="badge bg-secondary me-1">{cat}</span>
                                ))
                              : <span className="text-muted">—</span>}
                          </td>
                          <td>
                            {cls.isArchived ? (
                              <span className="badge bg-secondary">Archived</span>
                            ) : cls.took ? (
                              <span className="badge bg-success">Completed</span>
                            ) : (
                              <span className="badge bg-warning text-dark">Pending</span>
                            )}
                          </td>
                          <td className="text-muted" style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{createdAtFromRecord(cls)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Deleted classes panel */}
            <div className="px-3 pb-2 pt-1 border-top">
              <button className="btn btn-link btn-sm text-muted p-0" onClick={handleToggleDeletedClasses}>
                {showDeletedClasses ? '▲ Hide deleted classes' : '▼ Show deleted classes'}
              </button>
              {showDeletedClasses && (
                <div className="mt-2">
                  {deletedClassesLoading ? (
                    <div className="spinner-border spinner-border-sm" />
                  ) : deletedClasses.length === 0 ? (
                    <p className="text-muted small mb-0">No deleted classes.</p>
                  ) : (
                    <table className="table table-sm mb-0" style={{ fontSize: '0.8rem' }}>
                      <thead>
                        <tr><th>Course</th><th>Date</th><th>Credits</th><th>Deleted</th><th>Actions</th></tr>
                      </thead>
                      <tbody>
                        {deletedClasses.map((cls: any) => (
                          <tr key={cls._id} className="table-danger">
                            <td>{cls.classname}</td>
                            <td>{fmtDate(cls.classdate)}</td>
                            <td>{cls.classLU}</td>
                            <td>{fmtDate(cls.deletedAt)}</td>
                            <td className="d-flex gap-1">
                              <button
                                className="btn btn-sm btn-outline-success py-0 px-1"
                                disabled={classRestoring === cls._id}
                                onClick={() => handleRestoreClass(cls._id)}
                              >
                                {classRestoring === cls._id ? '…' : 'Restore'}
                              </button>
                              {isSuperAdmin && (
                                <button
                                  className="btn btn-sm btn-danger py-0 px-1"
                                  onClick={() => openPermDelete({
                                    entityType: 'Class',
                                    entityId: cls._id,
                                    entityLabel: cls.classname,
                                  })}
                                >
                                  Perm. Remove
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Email History Tab ── */}
      {activeTab === 'emails' && (
        <div className="card">
          <div className="card-header cec-section-header">
            <span className="cec-section-title">Transactional Email History</span>
            <small className="ms-2">system emails sent to this user</small>
          </div>
          <div className="card-body p-0">
            {emailLogsLoading && (
              <div className="text-center py-4">
                <div className="spinner-border spinner-border-sm" role="status" />
                <p className="mt-2 text-muted small mb-0">Loading email history…</p>
              </div>
            )}
            {!emailLogsLoading && emailLogsLoaded && emailLogs.length === 0 && (
              <div className="text-center py-4 text-muted small">No emails logged for this user yet.</div>
            )}
            {!emailLogsLoading && emailLogs.length > 0 && (
              <div className="table-responsive">
                <table className="table cec-table table-sm table-hover mb-0" style={{ fontSize: '0.82rem' }}>
                  <thead>
                    <tr>
                      {[['Date / Time', 160],['Type', 160],['Subject', undefined],['Status', 70]].map(([h, w]) => (
                        <th key={String(h)} style={{ width: w ? Number(w) : undefined }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {emailLogs.map((log: any, _eli: number) => {
                      const d = new Date(log.sentAt);
                      const dateStr = `${String(d.getUTCMonth()+1).padStart(2,'0')}/${String(d.getUTCDate()).padStart(2,'0')}/${d.getUTCFullYear()}`;
                      const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                      return (
                        <tr key={log._id}>
                          <td className="text-muted">{dateStr} {timeStr}</td>
                          <td>
                            <span className="badge bg-secondary" style={{ fontWeight: 500, letterSpacing: '0.02em' }}>
                              {log.emailType.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td style={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {log.subject || '—'}
                          </td>
                          <td>
                            {log.status === 'sent' ? (
                              <span className="badge bg-success">sent</span>
                            ) : log.status === 'skipped_optout' ? (
                              <span className="badge bg-secondary" title="User unsubscribed from this email type — not a failure">opted out</span>
                            ) : (
                              <span className="badge bg-danger" title={log.errorMessage}>failed</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Cycle create / edit modal ── */}
      {cycleModal !== 'closed' && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{cycleModal === 'create' ? 'Add Cycle' : 'Edit Cycle'}</h5>
                <button className="btn-close" onClick={closeCycleModal} />
              </div>
              <div className="modal-body">
                {cycleFormError && (
                  <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '0.85rem' }}>{cycleFormError}</div>
                )}
                <div className="mb-3">
                  <label className="form-label fw-semibold">Cycle Name <span className="text-danger">*</span></label>
                  <input
                    className="form-control"
                    value={cycleForm.cyclename}
                    onChange={e => setCycleForm(f => ({ ...f, cyclename: e.target.value }))}
                    placeholder="e.g. ARCH-GEORGIA-2027"
                  />
                </div>
                <div className="row g-3 mb-3">
                  <div className="col-6">
                    <label className="form-label fw-semibold">Profession</label>
                    <input
                      className="form-control"
                      value={cycleForm.professionID}
                      onChange={e => setCycleForm(f => ({ ...f, professionID: e.target.value }))}
                      placeholder="e.g. ARCH"
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-semibold">State ID <span className="text-danger">*</span></label>
                    <input
                      className="form-control"
                      type="number"
                      value={cycleForm.stateid}
                      onChange={e => setCycleForm(f => ({ ...f, stateid: e.target.value }))}
                      placeholder="e.g. 10 (Georgia)"
                    />
                  </div>
                </div>
                <div className="row g-3">
                  <div className="col-6">
                    <label className="form-label fw-semibold">Start Date</label>
                    <input
                      className="form-control"
                      type="date"
                      value={cycleForm.cyclestart}
                      onChange={e => setCycleForm(f => ({ ...f, cyclestart: e.target.value }))}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-semibold">End Date</label>
                    <input
                      className="form-control"
                      type="date"
                      value={cycleForm.cycleend}
                      onChange={e => setCycleForm(f => ({ ...f, cycleend: e.target.value }))}
                    />
                  </div>
                </div>
                <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.78rem' }}>
                  Leave dates blank to auto-fill from state requirement.
                </p>

                {cycleModal === 'edit' && cycleDeleteConfirming && (
                  <div className="mt-3 p-3 border border-danger rounded" style={{ background: 'rgba(220,53,69,0.06)' }}>
                    <p className="text-danger fw-semibold mb-2" style={{ fontSize: '0.85rem' }}>
                      ⚠ This will soft-delete the cycle. Classes are not affected.
                    </p>
                    <label className="form-label small mb-1">
                      Type <strong>{cycleForm.cyclename}</strong> to confirm:
                    </label>
                    <input
                      className="form-control form-control-sm"
                      value={cycleDeleteConfirmText}
                      onChange={e => setCycleDeleteConfirmText(e.target.value)}
                      placeholder={cycleForm.cyclename}
                      autoFocus
                    />
                  </div>
                )}
              </div>
              <div className="modal-footer d-flex justify-content-between align-items-center">
                <div>
                  {cycleModal === 'edit' && !cycleDeleteConfirming && (
                    <button className="btn btn-sm btn-outline-danger" onClick={() => setCycleDeleteConfirming(true)}>
                      Delete Cycle
                    </button>
                  )}
                  {cycleModal === 'edit' && cycleDeleteConfirming && (
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => { setCycleDeleteConfirming(false); setCycleDeleteConfirmText(''); }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={handleDeleteCycle}
                        disabled={cycleDeleteConfirmText !== cycleForm.cyclename || cycleDeleting}
                      >
                        {cycleDeleting ? 'Removing…' : 'Confirm Delete'}
                      </button>
                    </div>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <button className="btn btn-secondary" onClick={closeCycleModal}>Cancel</button>
                  {!cycleDeleteConfirming && (
                    <button className="btn btn-primary" onClick={handleSaveCycle} disabled={cycleSaving}>
                      {cycleSaving ? 'Saving…' : cycleModal === 'create' ? 'Create Cycle' : 'Save Changes'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── License create / edit modal ── */}
      {licenseModal !== 'closed' && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{licenseModal === 'create' ? 'Add License' : 'Edit License'}</h5>
                <button className="btn-close" onClick={closeLicenseModal} />
              </div>
              <div className="modal-body">
                {licenseFormError && (
                  <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '0.85rem' }}>{licenseFormError}</div>
                )}
                <div className="row g-3 mb-3">
                  <div className="col-6">
                    <label className="form-label fw-semibold">Profession <span className="text-danger">*</span></label>
                    <select
                      className="form-select"
                      value={licenseForm.professionID}
                      onChange={e => setLicenseForm(f => ({ ...f, professionID: e.target.value }))}
                    >
                      <option value="">Select Profession</option>
                      {professions.filter(p => p.isActive !== false).map(p => (
                        <option key={p.professionID} value={p.professionID}>{p.name} ({p.professionID})</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-semibold">State ID <span className="text-danger">*</span></label>
                    <input
                      className="form-control"
                      type="number"
                      value={licenseForm.stateID}
                      onChange={e => setLicenseForm(f => ({ ...f, stateID: e.target.value }))}
                      placeholder="e.g. 32 (NY)"
                    />
                  </div>
                </div>
                <div className="row g-3 mb-3">
                  <div className="col-8">
                    <label className="form-label fw-semibold">License Number <span className="text-danger">*</span></label>
                    <input
                      className="form-control"
                      value={licenseForm.licenseNumber}
                      onChange={e => setLicenseForm(f => ({ ...f, licenseNumber: e.target.value }))}
                      placeholder="e.g. RA-123456"
                    />
                  </div>
                  <div className="col-4">
                    <label className="form-label fw-semibold">Status</label>
                    <select
                      className="form-select"
                      value={licenseForm.status}
                      onChange={e => setLicenseForm(f => ({ ...f, status: e.target.value }))}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="expired">Expired</option>
                    </select>
                  </div>
                </div>
                <div className="row g-3">
                  <div className="col-6">
                    <label className="form-label fw-semibold">Issue Date</label>
                    <input
                      className="form-control"
                      type="date"
                      value={licenseForm.issueDate}
                      onChange={e => setLicenseForm(f => ({ ...f, issueDate: e.target.value }))}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-semibold">Expiry Date</label>
                    <input
                      className="form-control"
                      type="date"
                      value={licenseForm.expiryDate}
                      onChange={e => setLicenseForm(f => ({ ...f, expiryDate: e.target.value }))}
                    />
                  </div>
                </div>

                {licenseModal === 'edit' && licenseDeleteConfirming && (
                  <div className="mt-3 p-3 border border-danger rounded" style={{ background: 'rgba(220,53,69,0.06)' }}>
                    <p className="text-danger fw-semibold mb-2" style={{ fontSize: '0.85rem' }}>
                      ⚠ This will soft-delete the license. Associated cycles are not affected.
                    </p>
                    <label className="form-label small mb-1">
                      Type <strong>{licenseForm.licenseNumber}</strong> to confirm:
                    </label>
                    <input
                      className="form-control form-control-sm"
                      value={licenseDeleteConfirmText}
                      onChange={e => setLicenseDeleteConfirmText(e.target.value)}
                      placeholder={licenseForm.licenseNumber}
                      autoFocus
                    />
                  </div>
                )}
              </div>
              <div className="modal-footer d-flex justify-content-between align-items-center">
                <div>
                  {licenseModal === 'edit' && !licenseDeleteConfirming && (
                    <button className="btn btn-sm btn-outline-danger" onClick={() => setLicenseDeleteConfirming(true)}>
                      Delete License
                    </button>
                  )}
                  {licenseModal === 'edit' && licenseDeleteConfirming && (
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => { setLicenseDeleteConfirming(false); setLicenseDeleteConfirmText(''); }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={handleDeleteLicense}
                        disabled={licenseDeleteConfirmText !== licenseForm.licenseNumber || licenseDeleting}
                      >
                        {licenseDeleting ? 'Removing…' : 'Confirm Delete'}
                      </button>
                    </div>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <button className="btn btn-secondary" onClick={closeLicenseModal}>Cancel</button>
                  {!licenseDeleteConfirming && (
                    <button className="btn btn-primary" onClick={handleSaveLicense} disabled={licenseSaving}>
                      {licenseSaving ? 'Saving…' : licenseModal === 'create' ? 'Add License' : 'Save Changes'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Class create / edit modal ── */}
      {classModal !== 'closed' && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{classModal === 'create' ? 'Add Class' : 'Edit Class'}</h5>
                <button className="btn-close" onClick={closeClassModal} />
              </div>
              <div className="modal-body">
                {classFormError && (
                  <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '0.85rem' }}>{classFormError}</div>
                )}
                <div className="mb-3">
                  <label className="form-label fw-semibold">Course Title <span className="text-danger">*</span></label>
                  <input
                    className="form-control"
                    value={classForm.classname}
                    onChange={e => setClassForm(f => ({ ...f, classname: e.target.value }))}
                    placeholder="e.g. Introduction to Building Codes"
                  />
                </div>
                <div className="row g-3 mb-3">
                  <div className="col-6">
                    <label className="form-label fw-semibold">Date <span className="text-danger">*</span></label>
                    <input
                      className="form-control"
                      type="date"
                      value={classForm.classdate}
                      onChange={e => setClassForm(f => ({ ...f, classdate: e.target.value }))}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-semibold">Credits (LU)</label>
                    <input
                      className="form-control"
                      type="number"
                      step="0.25"
                      min="0"
                      value={classForm.classLU}
                      onChange={e => setClassForm(f => ({ ...f, classLU: e.target.value }))}
                      placeholder="e.g. 1.5"
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Categories</label>
                  <input
                    className="form-control"
                    value={classForm.categories}
                    onChange={e => setClassForm(f => ({ ...f, categories: e.target.value }))}
                    placeholder="e.g. HSW, ETHICS (comma-separated)"
                  />
                </div>
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="classTookCheck"
                    checked={classForm.took}
                    onChange={e => setClassForm(f => ({ ...f, took: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="classTookCheck">
                    Mark as completed (credits count toward compliance)
                  </label>
                </div>

                {classModal === 'edit' && classDeleteConfirming && (
                  <div className="mt-3 p-3 border border-danger rounded" style={{ background: 'rgba(220,53,69,0.06)' }}>
                    <p className="text-danger fw-semibold mb-2" style={{ fontSize: '0.85rem' }}>
                      ⚠ This will soft-delete the class. It will no longer count toward compliance.
                    </p>
                    <label className="form-label small mb-1">
                      Type <strong>{classForm.classname}</strong> to confirm:
                    </label>
                    <input
                      className="form-control form-control-sm"
                      value={classDeleteConfirmText}
                      onChange={e => setClassDeleteConfirmText(e.target.value)}
                      placeholder={classForm.classname}
                      autoFocus
                    />
                  </div>
                )}
              </div>
              <div className="modal-footer d-flex justify-content-between align-items-center">
                <div>
                  {classModal === 'edit' && !classDeleteConfirming && (
                    <button className="btn btn-sm btn-outline-danger" onClick={() => setClassDeleteConfirming(true)}>
                      Delete Class
                    </button>
                  )}
                  {classModal === 'edit' && classDeleteConfirming && (
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => { setClassDeleteConfirming(false); setClassDeleteConfirmText(''); }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={handleDeleteClass}
                        disabled={classDeleteConfirmText !== classForm.classname || classDeleting}
                      >
                        {classDeleting ? 'Removing…' : 'Confirm Delete'}
                      </button>
                    </div>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <button className="btn btn-secondary" onClick={closeClassModal}>Cancel</button>
                  {!classDeleteConfirming && (
                    <button className="btn btn-primary" onClick={handleSaveClass} disabled={classSaving}>
                      {classSaving ? 'Saving…' : classModal === 'create' ? 'Add Class' : 'Save Changes'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Permanent Delete Modal (super admin only) ── */}
      {permDeleteTarget && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.65)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-danger">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">⚠ Permanent Delete</h5>
                <button className="btn-close btn-close-white" onClick={closePermDelete} disabled={permDeleting} />
              </div>
              <div className="modal-body">
                <p className="mb-2">
                  You are about to <strong>permanently and irreversibly remove</strong>:
                </p>
                <div className="alert alert-danger py-2 mb-3">
                  <strong>{permDeleteTarget.entityType}</strong>: {permDeleteTarget.entityLabel}
                </div>
                <p className="text-muted small mb-3">
                  This action cannot be undone. An audit log entry will be created. The record must already be soft-deleted before it can be permanently removed.
                </p>
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Type <code>PERMANENTLY DELETE</code> to confirm:
                  </label>
                  <input
                    className="form-control"
                    value={permDeletePhrase}
                    onChange={e => setPermDeletePhrase(e.target.value)}
                    placeholder="PERMANENTLY DELETE"
                    autoFocus
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Your admin password:</label>
                  <input
                    className="form-control"
                    type="password"
                    value={permDeletePw}
                    onChange={e => setPermDeletePw(e.target.value)}
                    placeholder="Enter your admin password"
                  />
                </div>
                {permDeleteError && (
                  <div className="alert alert-danger py-2">{permDeleteError}</div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closePermDelete} disabled={permDeleting}>
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handlePermDelete}
                  disabled={
                    permDeletePhrase !== 'PERMANENTLY DELETE' ||
                    !permDeletePw.trim() ||
                    permDeleting
                  }
                >
                  {permDeleting ? 'Deleting…' : 'Permanently Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Change Plan Modal ── */}
      {showPlanModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Change Account Plan</h5>
                <button className="btn-close" onClick={() => setShowPlanModal(false)} disabled={planSaving} />
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-3">
                  Changing <strong>{fullName}</strong>'s plan. This takes effect immediately and bypasses Stripe.
                </p>
                <div className="mb-3">
                  <label className="form-label fw-semibold">New Plan</label>
                  <select
                    className="form-select"
                    value={planTarget}
                    onChange={e => setPlanTarget(e.target.value as 'free' | 'solo' | 'pro' | 'team')}
                  >
                    <option value="free">Free</option>
                    <option value="solo">Solo</option>
                    <option value="pro">Pro</option>
                    <option value="team">Team</option>
                  </select>
                </div>
                {planTarget === 'team' && (
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Team Tier</label>
                    <select
                      className="form-select"
                      value={planTier}
                      onChange={e => setPlanTier(e.target.value as 'starter' | 'firm' | 'practice')}
                    >
                      <option value="starter">Starter — 5 seats</option>
                      <option value="firm">Firm — 10 seats</option>
                      <option value="practice">Practice — 25 seats</option>
                    </select>
                    <div className="form-text text-muted">Company document will be created automatically. No Stripe required.</div>
                  </div>
                )}
                <div className="mb-3">
                  <label className="form-label fw-semibold">Your Admin Password</label>
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Enter your admin password to confirm"
                    value={planAdminPw}
                    onChange={e => setPlanAdminPw(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleSavePlan(); }}
                    autoFocus
                  />
                </div>
                {planError && <div className="alert alert-danger py-2">{planError}</div>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowPlanModal(false)} disabled={planSaving}>Cancel</button>
                <button className="btn btn-warning" onClick={handleSavePlan} disabled={planSaving}>
                  {planSaving ? 'Saving…' : `Set to ${planTarget.toUpperCase()}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Onboarding Email Modal ── */}
      {/* ── Profession-Activated Notify Modal ── */}
      {showNotifyModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-0">Notify: Official Requirements Available</h5>
                  <small className="text-muted">To: {user.email}</small>
                </div>
                <button className="btn-close" onClick={() => { setShowNotifyModal(false); setNotifySentMsg(''); setNotifyError(''); setNotifyPreviewHtml(''); }} />
              </div>
              <div className="modal-body">
                {/* Test mode warning */}
                {notifyTestMode?.active && (
                  <div className="alert alert-warning d-flex align-items-start gap-2 py-2 mb-3" role="alert">
                    <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>⚠️</span>
                    <div>
                      <strong>Email test mode is ON.</strong> This email will <strong>not</strong> be delivered to{' '}
                      <strong>{user.email}</strong>.
                      {notifyTestMode.allowedEmails.length > 0 && (
                        <> Only <strong>{notifyTestMode.allowedEmails.join(', ')}</strong> will receive it.</>
                      )}
                      {' '}Turn off test mode in{' '}
                      <a href="/admin/settings" target="_blank" rel="noreferrer">Admin Settings</a>{' '}
                      before sending to real users.
                    </div>
                  </div>
                )}
                <div className="row g-3 mb-3">
                  <div className="col-md-3">
                    <label className="form-label fw-semibold small">Profession ID</label>
                    <input
                      className="form-control form-control-sm"
                      value={notifyProfessionID}
                      onChange={e => setNotifyProfessionID(e.target.value.toUpperCase())}
                      placeholder="LCSW"
                    />
                  </div>
                  <div className="col-md-5">
                    <label className="form-label fw-semibold small">Profession Full Name</label>
                    <input
                      className="form-control form-control-sm"
                      value={notifyProfessionName}
                      onChange={e => setNotifyProfessionName(e.target.value)}
                      placeholder="Licensed Clinical Social Worker"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold small">States (comma-separated)</label>
                    <input
                      className="form-control form-control-sm"
                      value={notifyStatesInput}
                      onChange={e => setNotifyStatesInput(e.target.value)}
                      placeholder="Oregon, Alaska"
                    />
                    <div className="form-text">e.g. Oregon, Alaska, Pennsylvania</div>
                  </div>
                </div>
                <button
                  className="btn btn-sm btn-outline-secondary mb-3"
                  onClick={handleNotifyPreview}
                  disabled={notifyPreviewLoading || !notifyProfessionID.trim() || !notifyStatesInput.trim()}
                >
                  {notifyPreviewLoading ? '⏳ Loading…' : '👁 Preview Email'}
                </button>

                {notifyPreviewHtml ? (
                  <iframe
                    title="Email preview"
                    srcDoc={notifyPreviewHtml}
                    style={{ width: '100%', height: '600px', border: '1px solid #dee2e6', borderRadius: 4 }}
                    sandbox="allow-same-origin"
                  />
                ) : !notifyPreviewLoading && (
                  <div className="text-muted small py-4 text-center" style={{ background: '#f8f9fa', borderRadius: 4 }}>
                    Fill in the fields above and click Preview Email to see the rendered message.
                  </div>
                )}
              </div>
              <div className="modal-footer flex-column align-items-stretch gap-2">
                {notifySentMsg && (
                  <div className="alert alert-success py-2 mb-0 w-100">✅ {notifySentMsg}</div>
                )}
                {notifyError && (
                  <div className="alert alert-danger py-2 mb-0 w-100">{notifyError}</div>
                )}
                {!notifySentMsg && notifyPreviewHtml && (
                  <div className={`form-check w-100 mb-1 ${notifyTestMode?.active ? 'opacity-50' : ''}`}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="notifyConfirmCheck"
                      checked={notifyConfirmed}
                      onChange={e => setNotifyConfirmed(e.target.checked)}
                    />
                    <label className="form-check-label small" htmlFor="notifyConfirmCheck">
                      {notifyTestMode?.active
                        ? <>I understand test mode is ON — clicking Send will be <strong>blocked</strong> by the server</>
                        : <>I confirm I want to send this email to <strong>{fullName}</strong> at <strong>{user.email}</strong></>
                      }
                    </label>
                  </div>
                )}
                <div className="d-flex gap-2 justify-content-end w-100">
                  <button
                    className="btn btn-secondary"
                    onClick={() => { setShowNotifyModal(false); setNotifySentMsg(''); setNotifyError(''); setNotifyPreviewHtml(''); }}
                  >
                    Close
                  </button>
                  {!notifySentMsg && (
                    <button
                      className="btn btn-warning"
                      onClick={handleSendNotifyEmail}
                      disabled={!notifyConfirmed || notifySending || !notifyPreviewHtml}
                    >
                      {notifySending ? 'Sending…' : '✉️ Send Email'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showOnboardingModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-0">Onboarding Email Preview</h5>
                  <small className="text-muted">To: {onboardingCandidate?.userEmail || user.email}</small>
                </div>
                <button
                  className="btn-close"
                  onClick={() => { setShowOnboardingModal(false); setOnboardingSentMsg(''); setOnboardingError(''); }}
                />
              </div>
              <div className="modal-body p-0">
                {onboardingLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border spinner-border-sm" role="status" />
                    <p className="mt-2 text-muted small">Loading preview…</p>
                  </div>
                ) : onboardingPreviewHtml ? (
                  <iframe
                    title="Email preview"
                    srcDoc={onboardingPreviewHtml}
                    style={{ width: '100%', height: '620px', border: 'none' }}
                    sandbox="allow-same-origin"
                  />
                ) : null}
              </div>
              <div className="modal-footer flex-column align-items-stretch gap-2">
                {onboardingSentMsg && (
                  <div className="alert alert-success py-2 mb-0 w-100">✅ {onboardingSentMsg}</div>
                )}
                {onboardingError && (
                  <div className="alert alert-danger py-2 mb-0 w-100">{onboardingError}</div>
                )}
                {!onboardingSentMsg && (
                  <div className="form-check w-100 mb-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="onboardingConfirmCheck"
                      checked={onboardingConfirmed}
                      onChange={e => setOnboardingConfirmed(e.target.checked)}
                    />
                    <label className="form-check-label small" htmlFor="onboardingConfirmCheck">
                      I confirm I want to send this email to{' '}
                      <strong>{onboardingCandidate?.userName || fullName}</strong> at{' '}
                      <strong>{onboardingCandidate?.userEmail || user.email}</strong>
                    </label>
                  </div>
                )}
                <div className="d-flex gap-2 justify-content-end w-100">
                  <button
                    className="btn btn-secondary"
                    onClick={() => { setShowOnboardingModal(false); setOnboardingSentMsg(''); setOnboardingError(''); }}
                  >
                    Close
                  </button>
                  {!onboardingSentMsg && (
                    <button
                      className="btn btn-warning"
                      onClick={handleSendOnboardingEmail}
                      disabled={!onboardingConfirmed || onboardingSending || onboardingLoading}
                    >
                      {onboardingSending ? 'Sending…' : '✉️ Send Email'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Override Limits Modal ── */}
      {showOverrideModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Override Free-Plan Limits</h5>
                <button className="btn-close" onClick={() => setShowOverrideModal(false)} disabled={overrideSaving} />
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-3">
                  Grant <strong>{fullName}</strong> additional capacity while keeping their Free account.
                  Leave blank to use the default limit.
                </p>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Max Cycles</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Leave blank for default (1)"
                    min={1}
                    value={overrideMaxCycles}
                    onChange={e => setOverrideMaxCycles(e.target.value)}
                  />
                  <div className="form-text">Default free limit is 1. Enter a higher number to allow more.</div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Max States</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Leave blank — no state limit currently enforced"
                    min={1}
                    value={overrideMaxStates}
                    onChange={e => setOverrideMaxStates(e.target.value)}
                  />
                  <div className="form-text">Stored for future enforcement. No active state limit today.</div>
                </div>
                {overrideError && <div className="alert alert-danger py-2">{overrideError}</div>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowOverrideModal(false)} disabled={overrideSaving}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSaveOverrides} disabled={overrideSaving}>
                  {overrideSaving ? 'Saving…' : 'Save Overrides'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserView;
